import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import { RadioButton } from '@components/shared/radio-button/RadioButton';
import { FormField } from '@components/ui/form';
import { HealthQuestion } from '@features/onboarding/components/6-health-question-form/interfaces';
import { MedicationQuestionStep } from '@features/onboarding/components/6-health-question-form/components/medication-question/MedicationQuestionStep';
import { cn } from '@utils/index';
import { FormMessageSpan } from '@components/ui/form';
import { useHealthStore } from 'src/store/health';

interface TakingMedicationsNowStepProps {
  question: HealthQuestion;
  form: UseFormReturn<HealthQuestionsSchema>;
}

export const TakingMedicationsNowStep: React.FC<
  TakingMedicationsNowStepProps
> = ({ question, form }) => {
  const { isFailValidation } = useHealthStore();

  useEffect(() => {
    form.trigger('healthQuestions');
  }, [form]);

  const error =
    form.formState.errors?.healthQuestions?.takingMedicationsNow?.value
      ?.message;

  return (
    <>
      <div
        className={cn(
          'flex relative gap-20 max-xl:gap-8.5 p-6 items-center border border-transparent justify-between flex-col max-xl:text-center card-shadow bg-card'
        )}
      >
        <div className="w-full">
          <section className="flex max-xl:flex-col items-center max-xl:items-center justify-between w-full md:gap-2">
            <img
              src="/images/medication.svg"
              alt="medications-image"
              className="w-22 -mt-2.5 max-xl:hidden"
            />
            <h3 className="!font-secondary text-xl mb-4 max-w-135 max-md:text-[15px] max-md:mb-2.5 max-md:px-2.5 max-md:max-w-87.5">
              {question.question}
            </h3>
            <div className="flex flex-col space-x-4 gap-2 max-xl:flex-row max-xl:gap-2.5">
              <FormField
                control={form.control}
                name="healthQuestions.takingMedicationsNow.value"
                render={({ field }) => (
                  <>
                    <div
                      className={cn(
                        'radio-wrapper-date max-md:flex max-md:h-13.5',
                        field.value === true && 'radio-wrapper-checked'
                      )}
                    >
                      <RadioButton
                        label="YES"
                        name={question.id}
                        value="true"
                        labelProps={{
                          className: 'text-black xl:text-base text-sm shrink-0',
                        }}
                        checked={field.value === true}
                        onChange={() => field.onChange(true)}
                      />
                    </div>

                    <div
                      className={cn(
                        'radio-wrapper-date',
                        'max-w-23.5 max-md:flex max-md:h-13.5',
                        field.value === false && 'radio-wrapper-checked'
                      )}
                    >
                      <RadioButton
                        label="NO"
                        name={question.id}
                        value="false"
                        labelProps={{
                          className: 'text-black xl:text-base text-sm shrink-0',
                        }}
                        checked={field.value === false}
                        onChange={() => field.onChange(false)}
                      />
                    </div>
                  </>
                )}
              />
            </div>
          </section>
          {error && isFailValidation && (
            <p className="mt-4 text-center xl:text-left">
              <FormMessageSpan
                text={error}
              />
            </p>
          )}
        </div>
      </div>
      {form.watch('healthQuestions.takingMedicationsNow.value') === true && (
        <MedicationQuestionStep question={question} form={form} />
      )}
    </>
  );
};
